// Holiday events: Glymsgiving (Thanksgiving), Glymus (Christmas) and New Year's. Same idea as Glymtober (lib/glymtober.ts): a date window,
// a welcome popup, and a boosted daily Gooncoin login bonus. Each event also has several pieces of art that take over on set days, so the popup
// looks different through the month. Everything here is a DEFAULT: an admin can change the dates, the bonus and the day each picture starts
// (Admin > Modal Content > the holiday's card, or /api/admin/holiday-config), and the values read back on the next page load.
import { GLYMTOBER_DAILY_GOONCOIN, STANDARD_DAILY_GOONCOIN, isGlymtoberActiveWithConfig, type GlymtoberConfig } from "@/lib/glymtober"

export type HolidayId = "new_years" | "glymus" | "glymsgiving"

export interface HolidayImage {
  /** Public path of the picture. Fixed in code; only the start day is editable. */
  src: string
  /** The first day (month 1-12, day 1-31) this picture is used. It stays up until the next picture's start day. */
  fromMonth: number
  fromDay: number
  /** Where the spinning Gooncoin sits on this picture, as percent of the card (left/top of the coin, and its width). Fixed in code. */
  coin?: { left: number; top: number; width: number }
}

export interface HolidayEvent {
  id: HolidayId
  enabled: boolean
  /** 1-12 */
  startMonth: number
  startDay: number
  endMonth: number
  endDay: number
  /** Gooncoin per daily login while this event is on (the usual amount is STANDARD_DAILY_GOONCOIN). */
  dailyGooncoin: number
  images: HolidayImage[]
}

/** Gooncoin per day during every holiday event (owner, 2026-10-27: 10, not Glymtober's 15). */
export const HOLIDAY_DAILY_GOONCOIN = 10

/**
 * The order matters: when two events are on at once (New Year's starts Dec 30, Glymus runs to Dec 31), the FIRST one in this list wins,
 * so the more specific, shorter event goes first.
 */
export const DEFAULT_HOLIDAY_EVENTS: HolidayEvent[] = [
  {
    id: "new_years",
    enabled: true,
    startMonth: 12, startDay: 30, endMonth: 1, endDay: 1,
    dailyGooncoin: HOLIDAY_DAILY_GOONCOIN,
    images: [{ src: "/screens/new-years/glym_newyears.webp", fromMonth: 12, fromDay: 30 }],
  },
  {
    id: "glymus",
    enabled: true,
    startMonth: 12, startDay: 1, endMonth: 12, endDay: 31,
    dailyGooncoin: HOLIDAY_DAILY_GOONCOIN,
    images: [
      // Snowman: on the outstretched mitten (the spot the owner boxed on 2026-10-27).
      { src: "/screens/glymus/glym_christmas_snow.webp", fromMonth: 12, fromDay: 1, coin: { left: 26.4, top: 44.4, width: 14.5 } },
      // Grinch and Santa: on the gift box and on the plate of cookies. These two spots are a first guess.
      { src: "/screens/glymus/glym_christmas_grinch.webp", fromMonth: 12, fromDay: 11, coin: { left: 42.8, top: 51.5, width: 14.5 } },
      { src: "/screens/glymus/glym_christmas_santa.webp", fromMonth: 12, fromDay: 21, coin: { left: 69.8, top: 53.7, width: 14.5 } },
    ],
  },
  {
    id: "glymsgiving",
    enabled: true,
    startMonth: 11, startDay: 1, endMonth: 11, endDay: 30,
    dailyGooncoin: HOLIDAY_DAILY_GOONCOIN,
    images: [
      { src: "/screens/glymsgiving/glym_thanksgiving_cook.webp", fromMonth: 11, fromDay: 1 },
      // Thanksgiving 2026 is Thursday Nov 26: Glym is cooking until then, and stuffed on the sofa from Thanksgiving Day for the rest of the month.
      // (Thanksgiving moves each year: the 4th Thursday of November. Change this day in Admin > Modal Content if needed.)
      { src: "/screens/glymsgiving/glym_thanksgiving_fat.webp", fromMonth: 11, fromDay: 26 },
    ],
  },
]

export const HOLIDAY_CONFIG_SETTING_KEY = "holiday_events_config"

// A non-leap year, so "month/day" maps to one stable day number (Feb 29 is not an event day).
const dayOfYear = (month: number, day: number) => Math.round((Date.UTC(2001, month - 1, day) - Date.UTC(2001, 0, 1)) / 86_400_000)
/** Days from (fromMonth, fromDay) forward to (month, day), wrapping the new year. 0 = the same day. */
const daysSince = (fromMonth: number, fromDay: number, month: number, day: number) =>
  (dayOfYear(month, day) - dayOfYear(fromMonth, fromDay) + 365) % 365

export function isHolidayActive(event: HolidayEvent, date: Date = new Date()): boolean {
  if (!event.enabled) return false
  const cur = (date.getMonth() + 1) * 100 + date.getDate()
  const start = event.startMonth * 100 + event.startDay
  const end = event.endMonth * 100 + event.endDay
  return start <= end ? cur >= start && cur <= end : cur >= start || cur <= end
}

/** The event that is on today (the first one in list order), or null. */
export function activeHoliday(events: HolidayEvent[], date: Date = new Date()): HolidayEvent | null {
  return events.find((e) => isHolidayActive(e, date)) ?? null
}

/** The picture for this date: the one whose start day is the latest one that has already come, counting from the event's own first day. */
export function holidayImageFor(event: HolidayEvent, date: Date = new Date()): string {
  return holidayPictureFor(event, date)?.src ?? ""
}

export function holidayPictureFor(event: HolidayEvent, date: Date = new Date()): HolidayImage | undefined {
  const month = date.getMonth() + 1
  const day = date.getDate()
  const today = daysSince(event.startMonth, event.startDay, month, day)
  let best = event.images[0]
  let bestOffset = -1
  for (const image of event.images) {
    const offset = daysSince(event.startMonth, event.startDay, image.fromMonth, image.fromDay)
    if (offset <= today && offset > bestOffset) {
      best = image
      bestOffset = offset
    }
  }
  return best
}

/** The year the event's window STARTED in, so a window that crosses New Year (Dec 30 to Jan 1) is one event, not two. */
export function holidayWindowYear(event: HolidayEvent, date: Date = new Date()): number {
  const wraps = event.startMonth * 100 + event.startDay > event.endMonth * 100 + event.endDay
  const pastNewYear = wraps && date.getMonth() + 1 <= event.endMonth
  return date.getFullYear() - (pastNewYear ? 1 : 0)
}

/** The popup is shown once per holiday per year. */
export function holidaySeenKey(event: HolidayEvent, date: Date = new Date()) {
  return `modal:holiday_${event.id}_${holidayWindowYear(event, date)}`
}
export function localHolidaySeenKey(event: HolidayEvent, date: Date = new Date()) {
  return `holiday_${event.id}_${holidayWindowYear(event, date)}_dismissed`
}

/**
 * Gooncoin for today's daily login: Glymtober's amount while it is on, else the active holiday's amount, else the usual amount.
 * (The windows do not overlap, but Glymtober is checked first so it can never be lowered by a holiday.)
 */
export function dailyGooncoinAmount(glymtober: GlymtoberConfig, events: HolidayEvent[], date: Date = new Date()): number {
  if (isGlymtoberActiveWithConfig(glymtober, date)) return GLYMTOBER_DAILY_GOONCOIN
  const holiday = activeHoliday(events, date)
  return holiday ? holiday.dailyGooncoin : STANDARD_DAILY_GOONCOIN
}

const clampInt = (value: unknown, min: number, max: number, fallback: number) => {
  const n = typeof value === "number" ? value : parseInt(String(value))
  return Number.isFinite(n) && n >= min && n <= max ? Math.trunc(n) : fallback
}

/** Lays an admin's saved values over the defaults, event by event. Anything missing or out of range keeps its default, and the picture
 * paths always come from the defaults, so a bad save can never point a popup at a file that does not exist. */
export function mergeHolidayConfig(stored: unknown): HolidayEvent[] {
  const saved: any[] = Array.isArray((stored as any)?.events) ? (stored as any).events : []
  return DEFAULT_HOLIDAY_EVENTS.map((base) => {
    const s = saved.find((e) => e && e.id === base.id)
    if (!s) return base
    return {
      id: base.id,
      enabled: typeof s.enabled === "boolean" ? s.enabled : base.enabled,
      startMonth: clampInt(s.startMonth, 1, 12, base.startMonth),
      startDay: clampInt(s.startDay, 1, 31, base.startDay),
      endMonth: clampInt(s.endMonth, 1, 12, base.endMonth),
      endDay: clampInt(s.endDay, 1, 31, base.endDay),
      dailyGooncoin: clampInt(s.dailyGooncoin, 1, 100, base.dailyGooncoin),
      images: base.images.map((img, i) => ({
        src: img.src,
        coin: img.coin,
        fromMonth: clampInt(s.images?.[i]?.fromMonth, 1, 12, img.fromMonth),
        fromDay: clampInt(s.images?.[i]?.fromDay, 1, 31, img.fromDay),
      })),
    }
  })
}

/** Server-side read of the admin's saved holiday settings (the defaults on any error or if never saved). */
export async function fetchHolidayEvents(supabase: { from: (table: string) => any }): Promise<HolidayEvent[]> {
  try {
    const { data } = await supabase.from("admin_settings").select("setting_value").eq("setting_key", HOLIDAY_CONFIG_SETTING_KEY).single()
    return mergeHolidayConfig(data?.setting_value)
  } catch {
    return DEFAULT_HOLIDAY_EVENTS
  }
}
